// Context resolver: hydrates the WorkflowContext a run's Conditions test.
//
// CONTEXT_FIELD_CATALOG is the single list of fields an owner can reference
// in a Condition's "Skip if" rules: the builder renders its picker from it,
// and resolveContext() knows how to hydrate every entry. Add a field in both
// places (catalog row + resolver case), validateForPublish doesn't police
// fields, so an unknown field simply never trips (predicates fail open).
//
// Context is resolved FRESH at every Condition (not once at trigger time):
// the whole point of a checkpoint after a Wait is testing what changed while
// the run slept.

import type { WorkflowContext } from "./schema.ts";
// channelMap (not wiring.ts): dependency-free, so the frontend build, which
// compiles this file for the builder's field catalog. Never sees wiring's
// URL imports.
import { toGraphChannelType } from "../customer_graph/channelMap.ts";
import { parseApptInstantSg } from "./apptTime.ts";
import { isExplicitAttendanceConfirmation } from "./attendanceConfirmation.ts";
import { evaluateRecoveryEligibility } from "../complaints/recoveryOffers.ts";
import {
  completedCount,
  customerBookingsAtBusiness,
  hasOtherUpcoming,
} from "../booking/customerHistory.ts";

// deno-lint-ignore no-explicit-any
type DB = any;

/**
 * Graph contact behind a conversation, via channel_identifiers. THE shared
 * resolution used by the engine, the outreach-signal scanner, and anything
 * else mapping a conversation to a contact. The channel→channel_type map
 * stays in customer_graph/wiring.ts (its documented single source).
 */
export async function resolveConversationContact(
  admin: DB,
  businessId: string,
  conv: { channel: string; channel_user_id: string },
): Promise<string | null> {
  const channelType = toGraphChannelType(conv.channel);
  if (!channelType) return null;
  const { data } = await admin
    .from("channel_identifiers")
    .select("contact_id")
    .eq("business_id", businessId)
    .eq("channel_type", channelType)
    .eq("identifier_value", conv.channel_user_id)
    .maybeSingle();
  return (data?.contact_id as string | null) ?? null;
}

/** What a run is about. Trigger emitters fill what they know; resolvers make
 *  do with what's present (missing ids just leave fields undefined). */
export interface RunSubject {
  contact_id?: string;
  conversation_id?: string;
  booking_id?: string;
  customer_name?: string;
  listing_id?: string;
  listing_name?: string;
  channel?: string;
  channel_user_id?: string;
  booking_date?: string;
  booking_time?: string;
  case_id?: string;
  case_reference?: string;
  case_category?: string;
  case_severity?: string;
  case_status?: string;
  case_assigned_role?: string;
  concern_summary?: string;
  complaint_id?: string;
  complaint_reference?: string;
  complaint_source?: string;
  complaint_subject?: string;
  complaint_summary?: string;
  complaint_rating?: number;
  reviewer_name?: string;
  recovery_url?: string;
  /** Filled at send time when a message uses {{google_review_link}}. */
  google_review_link?: string;
  form_submission_id?: string;
  form_id?: string;
  form_name?: string;
  form_source?: "webhook" | "backfill" | "reconciliation";
  participant_name?: string;
  student_ids?: string[];
  form_new_contact?: boolean;
  form_new_customer?: boolean;
  form_new_student?: boolean;
  workflow_fields?: Record<string, unknown>;
  /** Appointment instant epoch-ms, when the trigger is booking-anchored. */
  appt_ms?: number;
  /** When the session ends, epoch-ms (Session finished runs). */
  session_end_ms?: number;
}

export interface ContextFieldDef {
  field: string;
  label: string;
  /** Operators that make sense for this field (builder picker). */
  operators: string[];
  /** For within_days-style operators: what the number means. */
  valueHint?: string;
  /** One-line picker help: what the field tests and when it's available. */
  description?: string;
}

export const CONTEXT_FIELD_CATALOG: ContextFieldDef[] = [
  {
    field: "conversation.hasReplied",
    label: "Customer has replied (since the last workflow message)",
    operators: ["is_true", "is_false"],
    description: "Any customer message after this workflow's last send.",
  },
  {
    field: "conversation.confirmedAttendance",
    label: "Customer explicitly confirmed attendance",
    operators: ["is_true", "is_false"],
    description:
      "A clear CONFIRM/YES or equally explicit attendance phrase; questions and cancellations do not count.",
  },
  {
    field: "conversation.staffReplied",
    label: "Your team replied (since the last workflow message)",
    operators: ["is_true", "is_false"],
    description:
      "A person on your team wrote in this conversation after this workflow's last send.",
  },
  {
    field: "customer.bookedSinceStart",
    label: "Customer booked (since the last workflow message)",
    operators: ["is_true", "is_false"],
    description:
      "A booking was made for this customer after this workflow's last send. Needs a known customer.",
  },
  {
    field: "customer.optedOutOfMarketing",
    label: "Customer opted out of messaging",
    operators: ["is_true", "is_false"],
    description: "Opt-out on the run's channel (sends are blocked regardless).",
  },
  {
    field: "customer.lastMessagedDaysAgo",
    label: "Days since we last messaged the customer",
    operators: ["within_days", "gt", "lte"],
    valueHint: "days",
    description: "How long ago our last outbound message landed.",
  },
  {
    field: "customer.lastAutomationMessagedDaysAgo",
    label: "Days since another automation messaged the customer",
    operators: ["within_days", "gt", "lte"],
    valueHint: "days",
    description:
      "How long ago the latest marketing-class automation message landed; ordinary replies and booking reminders do not count.",
  },
  {
    field: "customer.tags",
    label: "Customer's tags",
    operators: ["contains"],
    valueHint: "tag text",
    description: "Tags on the contact — including ones other workflows added.",
  },
  {
    field: "customer.isFirstBooking",
    label: "This is the customer's first booking",
    operators: ["is_true", "is_false"],
    description:
      "No other booking exists for this customer. Needs a booking on the run.",
  },
  {
    field: "customer.completedVisits",
    label: "Customer's completed visits",
    operators: ["gt", "lte", "eq"],
    valueHint: "count",
    description: "How many bookings this customer has completed.",
  },
  {
    field: "customer.hasUpcomingBooking",
    label: "Customer has another upcoming booking",
    operators: ["is_true", "is_false"],
    description: "A different confirmed booking today or later exists.",
  },
  {
    field: "form.isBackfill",
    label: "Form response is from an import",
    operators: ["is_true", "is_false"],
    description:
      "True for historical backfill or reconciliation, false for a live Tally webhook.",
  },
  {
    field: "form.createdNewCustomer",
    label: "Form response created a new customer",
    operators: ["is_true", "is_false"],
    description:
      "Dibs created a new guardian/customer CRM record from this response.",
  },
  {
    field: "form.createdNewParticipant",
    label: "Form response created a new participant",
    operators: ["is_true", "is_false"],
    description:
      "Dibs created at least one new student/participant from this response.",
  },
  {
    field: "form.id",
    label: "Tally form ID",
    operators: ["eq", "neq"],
    description: "The configured Tally form that produced this response.",
  },
  {
    field: "form.name",
    label: "Tally form name",
    operators: ["eq", "neq"],
    description: "The configured form's display name.",
  },
  {
    field: "form.field.program",
    label: "Mapped form program",
    operators: ["eq", "neq", "contains"],
    description:
      "A form field explicitly exposed to workflows with key “program”.",
  },
  {
    field: "form.field.level",
    label: "Mapped form level",
    operators: ["eq", "neq", "contains"],
    description:
      "A form field explicitly exposed to workflows with key “level”.",
  },
  {
    field: "form.field.goal",
    label: "Mapped form goal",
    operators: ["eq", "neq", "contains"],
    description:
      "A form field explicitly exposed to workflows with key “goal”.",
  },
  {
    field: "booking.status",
    label: "Booking status",
    operators: ["eq", "neq"],
    valueHint: "confirmed / cancelled / completed / no_show",
    description: "The run's booking as it stands right now.",
  },
  {
    field: "booking.hoursUntilSession",
    label: "Hours until the session starts",
    operators: ["gt", "lte"],
    valueHint: "hours",
    description:
      "Negative once the session has started. Needs a booking on the run.",
  },
  {
    field: "booking.totalPaid",
    label: "Amount paid on the booking",
    operators: ["gt", "lte", "eq"],
    valueHint: "amount",
    description: "0 for free or unpaid bookings.",
  },
  {
    field: "case.acknowledged",
    label: "Case has been acknowledged",
    operators: ["is_true", "is_false"],
    description: "A team member has accepted ownership of the case.",
  },
  {
    field: "case.acknowledgementOverdue",
    label: "Case acknowledgement is overdue",
    operators: ["is_true", "is_false"],
    description:
      "The acknowledgement deadline passed without a team member accepting the case.",
  },
  {
    field: "case.resolutionOverdue",
    label: "Case resolution is overdue",
    operators: ["is_true", "is_false"],
    description: "The case remains open after its resolution deadline.",
  },
  {
    field: "case.outreachPauseRecommended",
    label: "Case should pause customer outreach",
    operators: ["is_true", "is_false"],
    description:
      "The concern is sensitive enough to stop proactive sales and follow-ups.",
  },
  {
    field: "case.severity",
    label: "Case urgency",
    operators: ["eq", "neq", "in"],
    valueHint: "emergency / high / normal / low",
    description: "The case's current human-reviewable urgency.",
  },
  {
    field: "case.category",
    label: "Case category",
    operators: ["eq", "neq", "in"],
    valueHint:
      "clinical / safety / service / billing / privacy / booking / operational / other",
    description: "The case's current routing category.",
  },
  {
    field: "case.status",
    label: "Case status",
    operators: ["eq", "neq", "in"],
    valueHint:
      "open / acknowledged / in_progress / waiting_customer / resolved / closed",
    description: "Where the case currently sits in the human response process.",
  },
  {
    field: "complaint.recoveryOfferEligible",
    label: "Complaint qualifies for a recovery offer",
    operators: ["is_true", "is_false"],
    description:
      "The owner enabled a capped policy, the issue is non-sensitive, the customer is identified, and the frequency cap has room.",
  },
];

/**
 * Hydrate the context fields for one run.
 * `sinceIso`. The reference instant for "has the customer replied": the
 * engine passes the timestamp of the last message THIS RUN sent (or the run
 * start). `hasReplied` accepts any customer message; `confirmedAttendance`
 * requires explicit positive attendance intent.
 * `fields`, when given, only the named fields are hydrated (the engine
 * passes the union referenced by the Condition being evaluated, so a run
 * never pays for queries its checks don't use). Omit to hydrate everything.
 */
export async function resolveContext(
  admin: DB,
  businessId: string,
  subject: RunSubject,
  sinceIso: string,
  fields?: ReadonlySet<string>,
): Promise<WorkflowContext> {
  const ctx: WorkflowContext = {};
  const want = (f: string) => !fields || fields.has(f);

  if (
    subject.form_submission_id &&
    (
      want("form.isBackfill") || want("form.createdNewCustomer") ||
      want("form.createdNewParticipant") || want("form.id") ||
      want("form.name") ||
      [...(fields ?? [])].some((field) => field.startsWith("form.field."))
    )
  ) {
    ctx["form.isBackfill"] = subject.form_source !== "webhook";
    ctx["form.createdNewCustomer"] = subject.form_new_customer === true;
    ctx["form.createdNewParticipant"] = subject.form_new_student === true;
    ctx["form.id"] = subject.form_id ?? null;
    ctx["form.name"] = subject.form_name ?? null;
    for (const [key, value] of Object.entries(subject.workflow_fields ?? {})) {
      const field = `form.field.${key}`;
      if (want(field)) ctx[field] = value as WorkflowContext[string];
    }
  }

  // conversation.*. Needs a conversation.
  if (
    subject.conversation_id &&
    (want("conversation.hasReplied") ||
      want("conversation.confirmedAttendance"))
  ) {
    const { data: replies } = await admin
      .from("ai_messages")
      .select("content")
      .eq("conversation_id", subject.conversation_id)
      .eq("sender_type", "customer")
      .gt("created_at", sinceIso)
      .order("created_at", { ascending: false })
      .limit(100);
    const messages = (replies ?? []) as Array<{ content?: string }>;
    ctx["conversation.hasReplied"] = messages.length > 0;
    ctx["conversation.confirmedAttendance"] = messages.some((reply) =>
      isExplicitAttendanceConfirmation(reply.content)
    );
  }

  // conversation.staffReplied. Someone on the team wrote by hand (in Dibs or
  // the WhatsApp Business app) since the reference instant.
  if (subject.conversation_id && want("conversation.staffReplied")) {
    const { data: staff } = await admin
      .from("ai_messages")
      .select("id")
      .eq("conversation_id", subject.conversation_id)
      .eq("sender_type", "human_owner")
      .gt("created_at", sinceIso)
      .limit(1);
    ctx["conversation.staffReplied"] = (staff ?? []).length > 0;
  }

  // customer.bookedSinceStart. The Customer Graph records every booking as a
  // BookingCreated/BookingConfirmed event on the contact. Without a contact
  // the field stays undefined (predicates fail open).
  if (subject.contact_id && want("customer.bookedSinceStart")) {
    const { data: booked, error: bookedError } = await admin
      .from("contact_events")
      .select("event_id")
      .eq("business_id", businessId)
      .eq("contact_id", subject.contact_id)
      .in("event_type", ["BookingCreated", "BookingConfirmed"])
      .gt("occurred_at", sinceIso)
      .limit(1);
    if (!bookedError) ctx["customer.bookedSinceStart"] = (booked ?? []).length > 0;
  }

  // customer.optedOutOfMarketing. Either flag on the run's channel (or any
  // channel when the subject has none) blocks marketing outreach.
  // customer.tags. The same contacts read carries custom_attributes.tags.
  if (
    subject.contact_id &&
    (want("customer.optedOutOfMarketing") || want("customer.tags"))
  ) {
    const { data: contact } = await admin
      .from("contacts")
      .select("opt_outs, custom_attributes")
      .eq("contact_id", subject.contact_id)
      .maybeSingle();
    const optOuts = (contact?.opt_outs ?? {}) as Record<
      string,
      { marketing?: boolean; transactional?: boolean }
    >;
    const entries = subject.channel
      ? [optOuts[subject.channel]].filter(Boolean)
      : Object.values(optOuts);
    ctx["customer.optedOutOfMarketing"] = entries.some(
      (o) => o?.marketing === true || o?.transactional === true,
    );
    const attrs = (contact?.custom_attributes ?? {}) as Record<string, unknown>;
    ctx["customer.tags"] = Array.isArray(attrs.tags)
      ? (attrs.tags as string[])
      : [];
  }

  // customer.lastMessagedDaysAgo. Most recent non-customer message in this
  // conversation BEFORE this run's own sends (bounded by sinceIso so the
  // run doesn't count its own outreach).
  if (subject.conversation_id && want("customer.lastMessagedDaysAgo")) {
    const { data: lastOut } = await admin
      .from("ai_messages")
      .select("created_at")
      .eq("conversation_id", subject.conversation_id)
      .neq("sender_type", "customer")
      .lt("created_at", sinceIso)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    ctx["customer.lastMessagedDaysAgo"] = lastOut
      ? Math.floor(
        (Date.now() - new Date(lastOut.created_at).getTime()) / 86_400_000,
      )
      : null;
  }

  // customer.lastAutomationMessagedDaysAgo. The latest marketing-class
  // proactive send for this contact. This deliberately reads the shared
  // ledger instead of ai_messages: an ordinary human/AI reply is what starts
  // the idle-lead quiet period and must not be mistaken for "another
  // workflow". Transactional booking reminders are also intentionally
  // excluded, matching the send guard's cross-automation semantics.
  if (subject.contact_id && want("customer.lastAutomationMessagedDaysAgo")) {
    const { data: recentSends } = await admin
      .from("proactive_sends")
      .select("source, sent_at")
      .eq("business_id", businessId)
      .eq("contact_id", subject.contact_id)
      .order("sent_at", { ascending: false })
      .limit(20);
    const lastMarketingSend = (recentSends ?? []).find(
      (row: { source?: string }) =>
        row.source !== "reminder" && row.source !== "workflow_transactional",
    );
    ctx["customer.lastAutomationMessagedDaysAgo"] = lastMarketingSend?.sent_at
      ? Math.floor(
        (Date.now() - new Date(lastMarketingSend.sent_at as string).getTime()) /
          86_400_000,
      )
      : null;
  }

  // booking.* and booking-derived customer history. Need a booking. Missing
  // booking leaves the fields undefined (predicates fail open, by design).
  const wantBooking = want("booking.status") ||
    want("booking.hoursUntilSession") ||
    want("booking.totalPaid") || want("customer.isFirstBooking") ||
    want("customer.completedVisits") || want("customer.hasUpcomingBooking");
  if (subject.booking_id && wantBooking) {
    const { data: booking } = await admin
      .from("bookings")
      .select(
        "status, customer_id, customer_phone, customer_email, total_paid, booking_date, booking_time",
      )
      .eq("booking_id", subject.booking_id)
      .maybeSingle();
    ctx["booking.status"] = (booking?.status as string | null) ?? null;
    ctx["booking.totalPaid"] = Number(booking?.total_paid ?? 0);

    if (want("booking.hoursUntilSession")) {
      const apptMs = subject.appt_ms ??
        (booking?.booking_date
          ? parseApptInstantSg(booking.booking_date, booking.booking_time ?? "")
          : null);
      ctx["booking.hoursUntilSession"] = apptMs == null
        ? null
        : Math.round(((apptMs - Date.now()) / 3_600_000) * 10) / 10;
    }

    // Customer booking history at THIS business only (customerHistory.ts),
    // matched by customer link, phone or email. Without any of those, or if
    // the lookup fails, history fields stay undefined (fail open).
    const wantHistory = want("customer.isFirstBooking") ||
      want("customer.completedVisits") || want("customer.hasUpcomingBooking");
    const history = booking && wantHistory
      ? await customerBookingsAtBusiness(admin, businessId, {
        customerId: booking.customer_id,
        phone: booking.customer_phone,
        email: booking.customer_email,
      }).catch(() => null)
      : null;
    const hasIdentity = !!(booking?.customer_id || booking?.customer_phone?.trim() ||
      booking?.customer_email?.trim());

    if (history && hasIdentity) {
      if (want("customer.isFirstBooking")) {
        // "Any prior booking at all" semantics: ANY other booking row for this
        // customer here (whatever its status) means not-first.
        ctx["customer.isFirstBooking"] = !history.some((b) =>
          b.booking_id !== subject.booking_id
        );
      }
      if (want("customer.completedVisits")) {
        ctx["customer.completedVisits"] = completedCount(history);
      }
      if (want("customer.hasUpcomingBooking")) {
        const todaySg = new Date().toLocaleDateString("en-CA", {
          timeZone: "Asia/Singapore",
        });
        ctx["customer.hasUpcomingBooking"] = hasOtherUpcoming(
          history,
          subject.booking_id,
          todaySg,
        );
      }
    }
  }

  const wantCase = want("case.acknowledged") ||
    want("case.acknowledgementOverdue") || want("case.resolutionOverdue") ||
    want("case.outreachPauseRecommended") || want("case.severity") ||
    want("case.category") || want("case.status");
  if (subject.case_id && wantCase) {
    const { data: caseRow } = await admin
      .from("crisis_cases")
      .select(
        "status, category, severity, acknowledged_at, acknowledgement_due_at, resolution_due_at, outreach_pause_recommended",
      )
      .eq("business_id", businessId)
      .eq("id", subject.case_id)
      .maybeSingle();
    if (caseRow) {
      const now = Date.now();
      ctx["case.acknowledged"] = !!caseRow.acknowledged_at;
      ctx["case.acknowledgementOverdue"] = !caseRow.acknowledged_at &&
        new Date(caseRow.acknowledgement_due_at).getTime() < now;
      ctx["case.resolutionOverdue"] =
        !["resolved", "closed"].includes(caseRow.status) &&
        new Date(caseRow.resolution_due_at).getTime() < now;
      ctx["case.outreachPauseRecommended"] =
        caseRow.outreach_pause_recommended === true;
      ctx["case.severity"] = caseRow.severity;
      ctx["case.category"] = caseRow.category;
      ctx["case.status"] = caseRow.status;
    }
  }

  if (subject.complaint_id && want("complaint.recoveryOfferEligible")) {
    const eligibility = await evaluateRecoveryEligibility(
      admin,
      businessId,
      subject.complaint_id,
    );
    ctx["complaint.recoveryOfferEligible"] = eligibility.eligible;
  }

  return ctx;
}
